import { geoMercator, geoPath } from 'd3-geo'
import forEach from 'lodash/forEach'
import type { ParsedDataCandleStick } from './typings'

export interface Dictionary<T> {
  [index: string]: T
}

type AvgPriceData = {
  zeit: string
  name: string
  avg_price: number
  min_preis_qm?: number
  max_preis_qm?: number
  type?: string
  year?: string
  avg_min?: number
  avg_max?: number
}

type CalculateAvgPriceData = Dictionary<AvgPriceData[]>

export function parseData(
  data: AvgPriceData[],
  hasMinMax?: boolean
): ParsedDataCandleStick[] {
  return data.map((d, idx) => ({
    id: parseInt(d.year!),
    x: parseInt(d.year!),
    y: parseFloat(d.avg_price.toFixed(2)),
    low: hasMinMax
      ? parseFloat(d.avg_min!.toFixed(2))
      : parseFloat(d.avg_price.toFixed(2)),
    high: hasMinMax
      ? parseFloat(d.avg_max!.toFixed(2))
      : parseFloat(d.avg_price.toFixed(2)),
  }))
}

export function calculateAvgPrice(
  data: CalculateAvgPriceData,
  minMaxKey?: boolean
) {
  const avg_prices: any[] = []
  forEach(data, (dataForYear, year) => {
    if (!!minMaxKey) {
      avg_prices.push({
        year: year,
        avg_price:
          dataForYear.reduce((acc, cur) => acc + cur['avg_price'], 0) /
          dataForYear.length,
        avg_min:
          dataForYear.reduce((acc, cur) => acc + cur[`min_preis_qm`]!, 0) /
          dataForYear.length,
        avg_max:
          dataForYear.reduce((acc, cur) => acc + cur[`max_preis_qm`]!, 0) /
          dataForYear.length,
      })
    } else {
      avg_prices.push({
        year: year,
        avg_price:
          dataForYear.reduce((acc, cur) => acc + cur['avg_price'], 0) /
          dataForYear.length,
      })
    }
  })
  return avg_prices
}

export function stripQuarters(data: any) {
  return data.map((d: any) => ({
    ...d,
    zeit: d.zeit.split('-')[0],
  }))
}

export const calculateRelativeChange = (oldValue: number, newValue: number) => {
  return ((newValue - oldValue) / oldValue) * 100
}

export interface ScaleAndProjection {
  scale: number
  translate: [number, number]
}

export function getScaleAndProjection(
  features: any,
  width: number,
  height: number
): ScaleAndProjection {
  // Create a unit projection.
  const projection = geoMercator().scale(1).translate([0, 0])
  // Create a path generator.
  const path = geoPath().projection(projection)
  // Compute the bounds of a feature of interest, then derive scale & translate.
  const b = path.bounds(features)

  const scale =
    0.95 / Math.max((b[1][0] - b[0][0]) / width, (b[1][1] - b[0][1]) / height)

  // XXX we need to multiply by the width and height because nivo multiply this translation: https://github.com/plouc/nivo/blob/master/packages/geo/src/hooks.js#L57
  const translate: [number, number] = [
    (width - scale * (b[1][0] + b[0][0])) / 2 / width,
    (height - scale * (b[1][1] + b[0][1])) / 2 / height,
  ]

  // Update the projection to use computed scale & translate.
  return { scale, translate }
}
